import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Decodificador polyline independente de fallback para robustez
export function decodePolyline(encoded) {
    if (!encoded || typeof encoded !== 'string') return [];
    const points = [];
    let index = 0;
    const len = encoded.length;
    let lat = 0;
    let lng = 0;

    while (index < len) {
        let b;
        let shift = 0;
        let result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += dlat;

        shift = 0;
        result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += dlng;

        // Leaflet utiliza coordenadas no formato [lat, lng]
        points.push([lat / 1e5, lng / 1e5]);
    }
    return points;
}

// Cria ícones personalizados SVG para Largada e Chegada
const criarPinIcon = (cor, texto) => {
    return L.divIcon({
        className: 'custom-map-pin',
        html: `
            <div style="
                display: flex;
                flex-direction: column;
                align-items: center;
                transform: translate(-50%, -100%);
            ">
                <div style="
                    background-color: ${cor};
                    color: #fff;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 3px 8px;
                    border-radius: 9999px;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                    border: 2px solid #ffffff;
                    white-space: nowrap;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                ">
                    ${texto}
                </div>
                <div style="
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 7px solid ${cor};
                    margin-top: -1px;
                "></div>
            </div>
        `,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });
};

export const MapaTrajetoCorrida = ({ trajeto, altura = '450px' }) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);

    useEffect(() => {
        if (!trajeto || !trajeto.encoded_polyline || !mapContainerRef.current) return;

        // Se o mapa já foi inicializado no container, limpa antes
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
        }

        const centroInicial = trajeto.ponto_largada 
            ? [trajeto.ponto_largada.lat, trajeto.ponto_largada.lng] 
            : [-23.55052, -46.633308];

        // 1. Inicializa o mapa com Leaflet
        const map = L.map(mapContainerRef.current, {
            center: centroInicial,
            zoom: 14,
            zoomControl: true,
            scrollWheelZoom: 'center',
        });
        mapInstanceRef.current = map;

        // 2. Camada de mapa OpenStreetMap (estilo limpo OSM padrão com atribuição correta)
        const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
        });
        if (tileLayer && tileLayer.addTo) {
            tileLayer.addTo(map);
        }

        // 3. Decodifica pontos e adiciona Polyline com destaque na cor tema
        const pontos = decodePolyline(trajeto.encoded_polyline);

        if (pontos.length > 0) {
            const polyline = L.polyline(pontos, {
                color: '#FF5722',
                weight: 5,
                opacity: 0.9,
                lineJoin: 'round',
            });
            if (polyline && polyline.addTo) {
                polyline.addTo(map);
            }

            // 4. Enquadramento automático com base nos bounds ou no trajeto
            if (trajeto.bounds && L.latLng && L.latLngBounds) {
                const corner1 = L.latLng(trajeto.bounds.south, trajeto.bounds.west);
                const corner2 = L.latLng(trajeto.bounds.north, trajeto.bounds.east);
                map.fitBounds(L.latLngBounds(corner1, corner2), { padding: [40, 40] });
            } else if (polyline && polyline.getBounds) {
                map.fitBounds(polyline.getBounds(), { padding: [40, 40] });
            }
        }

        // 5. Marcador de Largada (Verde)
        if (trajeto.ponto_largada) {
            const markerLargada = L.marker([trajeto.ponto_largada.lat, trajeto.ponto_largada.lng], {
                icon: criarPinIcon('#10B981', 'Largada'),
            });
            if (markerLargada && markerLargada.addTo) {
                markerLargada.addTo(map);
            }
        }

        // 6. Marcador de Chegada (Vermelho)
        if (trajeto.ponto_chegada) {
            const markerChegada = L.marker([trajeto.ponto_chegada.lat, trajeto.ponto_chegada.lng], {
                icon: criarPinIcon('#EF4444', 'Chegada'),
            });
            if (markerChegada && markerChegada.addTo) {
                markerChegada.addTo(map);
            }
        }

        // Corrige tamanho de renderização do mapa caso container mude
        setTimeout(() => {
            map.invalidateSize();
        }, 100);

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [trajeto]);

    if (!trajeto || !trajeto.encoded_polyline) {
        return (
            <div className="flex flex-col items-center justify-center bg-slate-900/60 rounded-xl p-8 text-slate-400 border border-dashed border-urb-border text-center">
                <span className="text-3xl mb-2">🗺️</span>
                <p className="text-sm font-medium text-slate-300">Trajeto do percurso ainda não disponibilizado para esta corrida.</p>
                <p className="text-xs text-slate-500 mt-1">O organizador pode enviar o trajeto no formato .KML, .KMZ ou .GPX.</p>
            </div>
        );
    }

    return (
        <div className="relative rounded-xl overflow-hidden shadow-xl border border-urb-border bg-slate-950">
            {/* Badge com informações da corrida sobrepostas */}
            <div className="absolute top-3 left-3 z-[1000] bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-lg shadow border border-urb-border text-xs font-medium text-slate-200 flex flex-wrap items-center gap-3 pointer-events-none">
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
                    <strong>Largada</strong>
                    <span className="text-slate-500">•</span>
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span>
                    <strong>Chegada</strong>
                </span>
                {trajeto.distancia_km && (
                    <span className="border-l border-slate-700 pl-3 text-urb-primary font-bold">
                        📏 {trajeto.distancia_km} km
                    </span>
                )}
                {trajeto.formato_origem && (
                    <span className="border-l border-slate-700 pl-3 uppercase text-[10px] tracking-wider text-slate-400">
                        {trajeto.formato_origem}
                    </span>
                )}
            </div>

            {/* Container onde o Leaflet renderiza o OpenStreetMap */}
            <div 
                ref={mapContainerRef} 
                data-testid="osm-map"
                style={{ width: '100%', height: altura }} 
            />
        </div>
    );
};

export default MapaTrajetoCorrida;
